import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexBusinessLog} from '@/types';
import * as formatDate from '@/utils/date';

import MerchantLogsSummary from './MerchantLogsSummary';

/**
 * `meta.message` isn't one of `BusinessLogMetadata`'s explicitly-typed keys,
 * so it falls through the index signature's `BusinessLogMetadataValue`,
 * which includes a nested `BusinessLogMetadata` object. This only trusts the
 * types whose default stringification is meaningful, rendering a genuine
 * nested object as JSON rather than the uninformative `[object Object]`.
 */
const toDisplayString = (value: unknown): string => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return JSON.stringify(value);
};

const MerchantLogsTable = ({items}: {items: AfriexBusinessLog[]}) => {
  return (
    <Table containerDataCy="merchant-logs-table">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th className="pl-6">Date</Th>
          <Th>Error Code</Th>
          <Th>Message</Th>
          <Th>Business ID</Th>
          <Th>User ID</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.length === 0 ? (
          <Tr enableHover={false}>
            <Td
              colSpan={6}
              className="text-center py-6 text-gray-500"
              data-cy="merchant-logs-empty-state"
            >
              No logs found.
            </Td>
          </Tr>
        ) : (
          items.map((item, index) => (
            <MerchantLogsTableRow
              key={item.id ?? index}
              item={item}
              index={index}
            />
          ))
        )}
      </TBody>
    </Table>
  );
};

const MerchantLogsTableRow = ({
  item,
  index,
}: {
  item: AfriexBusinessLog;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const meta = item?.meta ?? {};
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr
        className={zebraRow}
        data-cy="merchant-logs-row"
        data-log-id={item.id}
      >
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            data-cy="merchant-logs-row-toggle"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td className="pl-6 font-medium" data-cy="merchant-logs-row-date">
          {formatDate.DDMonYYYY(item.createdAt || '', true)}
        </Td>
        <Td className="font-medium" data-cy="merchant-logs-row-error-code">
          {String(meta.code ?? '—')}
        </Td>
        <Td
          className="font-medium"
          data-cy="merchant-logs-row-message"
          title={toDisplayString(meta.message ?? item?.message ?? '')}
        >
          {/* Td never wraps, so an untruncated message set the width of the
              whole table, and of the expanded details with it. The full text
              stays in the cell's title and in the details. */}
          <p className="truncate max-w-xs">
            {toDisplayString(meta.message ?? item?.message ?? '—')}
          </p>
        </Td>
        <Td className="font-medium" data-cy="merchant-logs-row-business-id">
          {String(meta.businessId ?? '—')}
        </Td>
        <Td className="font-medium" data-cy="merchant-logs-row-user-id">
          {String(meta.userId ?? '—')}
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <MerchantLogsSummary item={item} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default MerchantLogsTable;
